<syrf-dialog-layout>
  <div class="version-section">
    <h3>Application</h3>

    <!-- Web Application -->
    @if (webVersion(); as web) {
      <div class="version-item">
        <div
          class="version-header"
          fxLayout="row"
          fxLayoutAlign="space-between center"
        >
          <span class="service-name">{{ web.name }}</span>
          <mat-icon
            [style.color]="getStatusColor(web.status)"
            [matTooltip]="web.status"
            [attr.aria-label]="'Status: ' + web.status"
          >
            {{ getStatusIcon(web.status) }}
          </mat-icon>
        </div>
        <div class="version-details">
          <div class="version-value">{{ web.version }}</div>
          @if (
            buildInfo.gitSha &&
            buildInfo.gitSha !== 'CHANGEME' &&
            buildInfo.gitSha !== 'unknown'
          ) {
            <div class="version-meta">
              <span class="label">Commit:</span>
              <code>{{ buildInfo.gitSha | slice: 0 : 7 }}</code>
            </div>
          }
          @if (envInfo()?.environment || buildInfo.environment) {
            <div class="version-meta">
              <span class="label">Environment:</span>
              <span
                class="environment-badge"
                [class]="
                  'env-' +
                  (
                    envInfo()?.environment || buildInfo.environment
                  ).toLowerCase()
                "
              >
                {{ envInfo()?.environment || buildInfo.environment }}
              </span>
            </div>
          }
          @if (envInfo()?.prNumber) {
            <div class="version-meta">
              <span class="label">Pull Request:</span>
              <span class="pr-badge">#{{ envInfo()?.prNumber }}</span>
            </div>
          }
          @if (
            envInfo()?.databaseName && envInfo()?.databaseName !== 'unknown'
          ) {
            <div class="version-meta">
              <span class="label">Database:</span>
              <code [class.database-production]="isProductionDatabase()">{{
                $safeNavigationMigration(envInfo()?.databaseName)
                  | databaseDisplayName
                    : $safeNavigationMigration(envInfo()?.databaseType)
              }}</code>
              @if (isNonProductionDatabase()) {
                <button
                  mat-icon-button
                  matExpIconButton
                  class="database-warning-btn"
                  (click)="openEnvironmentWarning()"
                  matTooltip="This is not the production database. Click for details."
                  matTooltipPosition="above"
                  aria-label="View non-production database warning"
                >
                  <mat-icon class="database-warning-icon">warning</mat-icon>
                </button>
              }
            </div>
            @if (envInfo()?.databaseLocation; as loc) {
              <div class="version-meta">
                <span class="label">Location:</span>
                <code>{{ loc }}</code>
              </div>
            }
            @if (isProductionDatabase()) {
              <div class="version-meta production-warning">
                <mat-icon class="production-warning-icon">error</mat-icon>
                <span class="production-warning-text"
                  >Changes will persist to the live system</span
                >
              </div>
            }
          }
        </div>
      </div>
    } @else {
      <div class="version-item">
        <div
          class="version-header"
          fxLayout="row"
          fxLayoutAlign="space-between center"
        >
          <span class="service-name">Web Application</span>
          <mat-spinner diameter="20"></mat-spinner>
        </div>
        <div class="version-details">
          <div class="version-value loading">Loading...</div>
        </div>
      </div>
    }
  </div>

  <div class="version-section">
    <h3>Backend Services</h3>

    <!-- API Service -->
    @if (apiVersion(); as api) {
      <div class="version-item">
        <div
          class="version-header"
          fxLayout="row"
          fxLayoutAlign="space-between center"
        >
          <span class="service-name">{{ api.name }}</span>
          @if (api.status === 'loading') {
            <mat-spinner diameter="20"></mat-spinner>
          } @else {
            <mat-icon
              [style.color]="getStatusColor(api.status)"
              [matTooltip]="api.healthStatus || api.status"
              [attr.aria-label]="'Status: ' + api.status"
            >
              {{ getStatusIcon(api.status) }}
            </mat-icon>
          }
        </div>
        <div class="version-details">
          @if (api.status === 'loading') {
            <div class="version-value loading">Checking...</div>
          } @else if (api.status === 'not-configured') {
            <div class="version-value not-configured">
              Not available in local development
            </div>
          } @else if (api.status === 'unavailable') {
            <div class="version-value unavailable">Service unreachable</div>
          } @else if (api.version) {
            <div class="version-value">{{ api.version }}</div>
          }
        </div>
      </div>
    } @else {
      <div class="version-item">
        <div
          class="version-header"
          fxLayout="row"
          fxLayoutAlign="space-between center"
        >
          <span class="service-name">API Service</span>
          <mat-spinner diameter="20"></mat-spinner>
        </div>
        <div class="version-details">
          <div class="version-value loading">Checking...</div>
        </div>
      </div>
    }

    <!-- PM Service -->
    @if (pmVersion(); as pm) {
      <div class="version-item">
        <div
          class="version-header"
          fxLayout="row"
          fxLayoutAlign="space-between center"
        >
          <span class="service-name">{{ pm.name }}</span>
          @if (pm.status === 'loading') {
            <mat-spinner diameter="20"></mat-spinner>
          } @else {
            <mat-icon
              [style.color]="getStatusColor(pm.status)"
              [matTooltip]="pm.healthStatus || pm.status"
              [attr.aria-label]="'Status: ' + pm.status"
            >
              {{ getStatusIcon(pm.status) }}
            </mat-icon>
          }
        </div>
        <div class="version-details">
          @if (pm.status === 'loading') {
            <div class="version-value loading">Checking...</div>
          } @else if (pm.status === 'not-configured') {
            <div class="version-value not-configured">
              Not available in local development
            </div>
          } @else if (pm.status === 'unavailable') {
            <div class="version-value unavailable">Service unreachable</div>
          } @else if (pm.version) {
            <div class="version-value">{{ pm.version }}</div>
          }
        </div>
      </div>
    } @else {
      <div class="version-item">
        <div
          class="version-header"
          fxLayout="row"
          fxLayoutAlign="space-between center"
        >
          <span class="service-name">Project Management</span>
          <mat-spinner diameter="20"></mat-spinner>
        </div>
        <div class="version-details">
          <div class="version-value loading">Checking...</div>
        </div>
      </div>
    }

    <!-- Quartz Scheduler -->
    @if (quartzVersion(); as quartz) {
      <div class="version-item">
        <div
          class="version-header"
          fxLayout="row"
          fxLayoutAlign="space-between center"
        >
          <span class="service-name">{{ quartz.name }}</span>
          @if (quartz.status === 'loading') {
            <mat-spinner diameter="20"></mat-spinner>
          } @else {
            <mat-icon
              [style.color]="getStatusColor(quartz.status)"
              [matTooltip]="quartz.healthStatus || quartz.status"
              [attr.aria-label]="'Status: ' + quartz.status"
            >
              {{ getStatusIcon(quartz.status) }}
            </mat-icon>
          }
        </div>
        <div class="version-details">
          @if (quartz.status === 'loading') {
            <div class="version-value loading">Checking...</div>
          } @else if (quartz.status === 'not-configured') {
            <div class="version-value not-configured">
              Not available in local development
            </div>
          } @else if (quartz.status === 'unavailable') {
            <div class="version-value unavailable">Service unreachable</div>
          } @else if (quartz.version) {
            <div class="version-value">{{ quartz.version }}</div>
          }
        </div>
      </div>
    } @else {
      <div class="version-item">
        <div
          class="version-header"
          fxLayout="row"
          fxLayoutAlign="space-between center"
        >
          <span class="service-name">Quartz Scheduler</span>
          <mat-spinner diameter="20"></mat-spinner>
        </div>
        <div class="version-details">
          <div class="version-value loading">Checking...</div>
        </div>
      </div>
    }

    <!-- S3 Notifier (Lambda) -->
    @if (s3NotifierVersion(); as s3) {
      <div class="version-item">
        <div
          class="version-header"
          fxLayout="row"
          fxLayoutAlign="space-between center"
        >
          <span class="service-name">{{ s3.name }}</span>
          <mat-icon
            [style.color]="getStatusColor(s3.status)"
            [matTooltip]="
              s3.status === 'not-configured'
                ? 'Version not available'
                : s3.status
            "
            [attr.aria-label]="'Status: ' + s3.status"
          >
            {{ getStatusIcon(s3.status) }}
          </mat-icon>
        </div>
        <div class="version-details">
          @if (s3.status === 'not-configured') {
            <div class="version-value not-configured">
              Version info not available
            </div>
          } @else if (s3.version) {
            <div class="version-value">{{ s3.version }}</div>
          }
        </div>
      </div>
    } @else {
      <div class="version-item">
        <div
          class="version-header"
          fxLayout="row"
          fxLayoutAlign="space-between center"
        >
          <span class="service-name">S3 Notifier (Lambda)</span>
          <mat-spinner diameter="20"></mat-spinner>
        </div>
        <div class="version-details">
          <div class="version-value loading">Checking...</div>
        </div>
      </div>
    }

    <!-- Live Updates (SignalR) -->
    <div class="version-item">
      <div
        class="version-header"
        fxLayout="row"
        fxLayoutAlign="space-between center"
      >
        <span class="service-name">Live Updates</span>
        <mat-icon
          [style.color]="getStatusColor(liveUpdatesStatus())"
          [matTooltip]="liveUpdatesTooltip()"
          [attr.aria-label]="'Status: ' + liveUpdatesStatus()"
        >
          {{ getStatusIcon(liveUpdatesStatus()) }}
        </mat-icon>
      </div>
      <div class="version-details">
        <div
          class="version-value"
          [class.not-configured]="!liveUpdatesEnabled()"
          [class.unavailable]="liveUpdatesEnabled() && !liveUpdatesConnected()"
        >
          {{ liveUpdatesLabel() }}
        </div>
      </div>
    </div>
  </div>

  @if (
    buildInfo.gitInformationalVersion &&
    buildInfo.gitInformationalVersion !== 'CHANGEME'
  ) {
    <div class="version-section">
      <h3>Build Details</h3>
      <div class="build-details">
        <code class="full-version">{{
          buildInfo.gitInformationalVersion
        }}</code>
      </div>
    </div>
  }
  <ng-template syrfDialogActions>
    <div class="version-info-actions">
      <button mat-button matExpButton [syrfDialogClose]="null">Close</button>
      <div class="version-info-actions-end">
        <button
          mat-button
          matExpButton
          (click)="copyToClipboard()"
          matTooltip="Copy version info to clipboard"
        >
          <mat-icon>content_copy</mat-icon>
          Copy
        </button>
        <button
          mat-flat-button
          matExpButton
          color="primary"
          (click)="refresh()"
          matTooltip="Refresh version info"
        >
          <mat-icon>refresh</mat-icon>
          Refresh
        </button>
      </div>
    </div>
  </ng-template>
</syrf-dialog-layout>
